import { FormControlLabel, Typography, IconButton, Box } from '@mui/material';
import { Checkbox } from '@mui/material';
import { Paper } from '@mui/material';
import { FormGroup } from '@mui/material';
import { useState, useMemo } from 'react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Order, User } from '@protos/v2/order';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export type OrderField = keyof Order | keyof User;

export interface OrderFilterConfig {
  field: OrderField;
  label: string;
  options: FilterOption[];
  getValue: (order: Order) => string;
}

interface OrderFieldFilterProps {
  config: OrderFilterConfig;
  selectedValues: string[];
  onFilterChange: (value: string) => void;
  onSelectAll: () => void;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

export const OrderFieldFilter = ({
  config,
  selectedValues,
  onFilterChange,
  onSelectAll,
  isExpanded = false,
  onToggleExpanded,
}: OrderFieldFilterProps) => {
  const [internalExpanded, setInternalExpanded] = useState(false);

  const expanded = onToggleExpanded !== undefined ? isExpanded : internalExpanded;
  const setExpanded = onToggleExpanded !== undefined ? onToggleExpanded : () => setInternalExpanded(!internalExpanded);

  const allSelected = useMemo(() => selectedValues?.length === config.options.length, [selectedValues, config.options]);
  const someSelected = useMemo(() => selectedValues?.length > 0 && selectedValues?.length < config.options.length, [selectedValues, config.options]);

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        transition: 'all 0.3s ease-in-out !important',
        height: expanded ? '350px' : '65px',
        overflow: 'hidden',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" onClick={setExpanded} sx={{ cursor: 'pointer' }}>
        <Typography variant="subtitle2">Filter by {config.label}</Typography>
        <IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
      </Box>

      <FormGroup sx={{ mt: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={allSelected} indeterminate={someSelected} onChange={onSelectAll} size="small" disableRipple />}
          label="Select All"
        />
        <Box sx={{ ml: 2, maxHeight: '250px', overflowY: 'auto' }}>
          {config.options.map(option => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox disableRipple checked={selectedValues.includes(option.value)} onChange={() => onFilterChange(option.value)} size="small" />
              }
              label={
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <span>{option.label}</span>
                  {option.count !== undefined && (
                    <Typography variant="caption" color="text.secondary">
                      ({option.count})
                    </Typography>
                  )}
                </Box>
              }
            />
          ))}
        </Box>
      </FormGroup>
    </Paper>
  );
};
